import React, { useState } from 'react';
import { VoucherGroundService } from 'services/ItineraryManagerApi/types';
import Editor from 'pureUi/Editor';
import { SlideToggle } from 'ui/SlideToggle';
import { AccordionStackable } from 'ui/AccordionStackable/AccordionStackable';
import { useModal } from 'hooks/useModal';
import { VoucherAddUploadModal } from './VoucherAddUploadModal';
import { CircleIconButton } from 'ui/CircleIconButton';
import FluidButton from 'ui/FluidButton';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import classNames from 'classnames';
import { ProductEditUnderFold } from './common';

interface IVoucherGroundServiceEditProps {
  groundServices: VoucherGroundService[];
  voucherUuid: string;
  onVisibilityToggle: (groundServiceUuid: string, isVisible: boolean) => void;
  onDescriptionChange: (groundServiceUuid: string, description: string) => void;
  onMoreTextChange: (groundServiceUuid: string, moreText: string) => void;
  onAddUpload: (groundServiceUuid: string, uploadData: any) => void;
  onRemoveUpload: (groundServiceUuid: string, uploadIndex: number) => void;
}

export const VoucherGroundServiceEdit: React.FC<IVoucherGroundServiceEditProps> = ({
  groundServices,
  voucherUuid,
  onVisibilityToggle,
  onDescriptionChange,
  onMoreTextChange,
  onAddUpload,
  onRemoveUpload,
}) => {
  const [openGroundServices, setOpenGroundServices] = useState<string[]>([]);
  const addUploadModalOps = useModal<{
    uploadType: 'image' | 'file';
    groundServiceUuid: string;
  }>();

  if (groundServices.length === 0) return null;

  const handleToggle = (groundServiceUuid: string, isOpen: boolean) => {
    if (isOpen) {
      setOpenGroundServices([...openGroundServices, groundServiceUuid]);
    } else {
      setOpenGroundServices(openGroundServices.filter(uuid => uuid !== groundServiceUuid));
    }
  };

  return (
    <div className="mb-8">
      <span className="font-pt-sans font-bold text-[17px] text-black mb-4 block">Ground Services</span>
      <div>
        {groundServices.map(groundService => (
          <AccordionStackable
            key={groundService.uuid}
            isOpen={openGroundServices.includes(groundService.uuid)}
            onToggle={isOpen => handleToggle(groundService.uuid, isOpen)}
            renderSummary={({ isOpen }) => (
              <span className="flex items-center justify-between w-full pr-4">
                <span
                  className={classNames('text-base font-pt-sans', {
                    'font-bold': isOpen,
                    'font-medium': !isOpen,
                  })}
                >
                  {groundService.title}
                </span>
                <SlideToggle
                  id={`toggle-ground-service-${groundService.uuid}`}
                  checked={groundService.isVisible}
                  onActivate={() => onVisibilityToggle(groundService.uuid, true)}
                  onDeactivate={() => onVisibilityToggle(groundService.uuid, false)}
                  labelClassName={!groundService.isVisible ? 'bg-gray-20' : 'bg-teal-40'}
                  buttonClassName={!groundService.isVisible ? 'bg-gray-100' : 'bg-teal-100'}
                  onClick={e => e.stopPropagation()}
                />
              </span>
            )}
          >
            <div className="space-y-4">
              <ProductEditUnderFold product={groundService} />

              <div className="relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">Description</p>
                <Editor
                  handleEditorChange={content => onDescriptionChange(groundService.uuid, content)}
                  text={groundService.description || ''}
                  options={[]}
                  placeHolder="Write ground service description..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>
              <div className="relative">
                <p className="text-sm font-pt-sans text-gray-100 mb-2">More Text</p>
                <Editor
                  handleEditorChange={content => onMoreTextChange(groundService.uuid, content)}
                  text={groundService.moreText || ''}
                  options={[]}
                  placeHolder="Write additional ground service information..."
                  minHeight="125px"
                  initVals={{
                    autoresize_bottom_margin: 0,
                    min_height: 125,
                  }}
                />
              </div>

              {/* Existing uploads */}
              {groundService.uploads.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-pt-sans text-gray-100 mb-2">Attachments</p>
                  <div className="flex gap-2 flex-wrap">
                    {groundService.uploads.map((upload, uploadIdx) => (
                      <div key={uploadIdx} className="relative">
                        <span className="bg-gray-10 text-12px text-gray-100 p-10px w-[90px] h-[90px] flex items-center">
                          <span className="w-full line-clamp-3">{upload.displayName}</span>
                        </span>
                        <CircleIconButton
                          className="absolute top-2 right-2"
                          IconComponent={XIcon}
                          iconSize="sm"
                          type="secondary"
                          onClick={() => onRemoveUpload(groundService.uuid, uploadIdx)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add attachment button */}
              <div className="mt-4">
                <FluidButton
                  onClick={async () => {
                    const result = await addUploadModalOps.requestConfirmation({
                      uploadType: 'file',
                      groundServiceUuid: groundService.uuid,
                    });
                    if (result) {
                      onAddUpload(groundService.uuid, result);
                    }
                  }}
                  type="secondary"
                >
                  Attach File
                </FluidButton>
              </div>
            </div>
          </AccordionStackable>
        ))}
      </div>

      {/* Upload Modal */}
      <VoucherAddUploadModal
        voucherUuid={voucherUuid}
        isOpen={addUploadModalOps.isOpen}
        onClose={addUploadModalOps.handleClose}
        uploadType={addUploadModalOps.modalData?.uploadType || 'file'}
        handleConfirm={addUploadModalOps.handleConfirm}
        modalTitle="Add File to Ground Service"
        productType="ground service"
      />
    </div>
  );
};
